import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiUrl } from "../config/api";
import { clearClientSession, getAuthHeaders } from "../config/auth";
import {
  AppShell,
  ButtonBase,
  CardPanel,
  DataTable,
  FilterBar,
  PanelTitleRow,
  PaginationBar,
  TopbarActionGroup,
} from "../components";
import useRowSelection from "../hooks/useRowSelection";

const defaultFilters = {
  dateFrom: "",
  dateTo: "",
  studentName: "",
};

const ManageStudentPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const navigate = useNavigate();
  const studentSelection = useRowSelection(students, (student) => student.id);
  const selectedStudents = studentSelection.selectedIds;

  const handleLogout = () => {
    clearClientSession();
    navigate("/");
  };

  const fetchStudents = useCallback(async () => {
    setLoading(true);

    try {
      const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (appliedFilters.studentName) {
        query.append("studentName", appliedFilters.studentName);
      }

      if (appliedFilters.dateFrom) {
        query.append("dateFrom", appliedFilters.dateFrom);
      }

      if (appliedFilters.dateTo) {
        query.append("dateTo", appliedFilters.dateTo);
      }

      const response = await fetch(
        `${apiUrl("/api/students/summary")}?${query.toString()}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        },
      );

      const result = await response.json();

      setStudents(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error("Error fetching students:", err);
      setStudents([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, limit, page]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const confirmDelete = (id) => {
    setStudentToDelete(id);
  };

  const handleDelete = async () => {
    try {
      await fetch(`${apiUrl("/api/students")}/${studentToDelete}`, {
        method: "DELETE",
        headers: {
          ...getAuthHeaders(),
        },
      });

      setStudentToDelete(null);
      studentSelection.setSelectedIds((current) =>
        current.filter((id) => id !== studentToDelete),
      );
      fetchStudents();
    } catch (err) {
      console.error("Error deleting student:", err);
    }
  };

  const confirmBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(
        selectedStudents.map((id) =>
          fetch(`${apiUrl("/api/students")}/${id}`, {
            method: "DELETE",
            headers: {
              ...getAuthHeaders(),
            },
          }),
        ),
      );

      studentSelection.clearSelection();
      setShowBulkDeleteModal(false);
      fetchStudents();
    } catch (err) {
      console.error("Error deleting selected students:", err);
    }
  };

  const handleClear = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [
        [
          "Student Name",
          "Student Email",
          "Phone Number",
          "Total Attendance",
          "Total Absent",
        ],
      ],
      body: students.map((student) => [
        student.name,
        student.email,
        student.phone,
        student.total_attendance,
        student.total_absent,
      ]),
    });

    doc.save("students_summary.pdf");
  };

  const exportToExcel = () => {
    const csvContent = [
      [
        "Student Name",
        "Student Email",
        "Phone Number",
        "Total Attendance",
        "Total Absent",
      ],
      ...students.map((student) => [
        student.name,
        student.email,
        student.phone,
        student.total_attendance,
        student.total_absent,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "students_summary.csv");
  };

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          className="ui-checkbox"
          checked={studentSelection.isAllSelected}
          onChange={studentSelection.toggleAllVisible}
        />
      ),
      mobileLabel: "Select",
      render: (student) => (
        <input
          type="checkbox"
          className="ui-checkbox"
          checked={selectedStudents.includes(student.id)}
          onChange={() => studentSelection.toggleSelection(student.id)}
        />
      ),
    },
    {
      key: "name",
      label: "Student Name",
    },
    {
      key: "email",
      label: "Student Email",
      className: "ui-data-table__muted",
    },
    {
      key: "phone",
      label: "Phone Number",
      className: "ui-data-table__muted",
    },
    {
      key: "total_attendance",
      label: "Total Attendance",
      className: "ui-data-table__muted",
    },
    {
      key: "total_absent",
      label: "Total Absent",
      className: "ui-data-table__muted",
    },
    {
      key: "actions",
      label: "Action",
      mobileLabel: "Action",
      render: (student) => (
        <button
          type="button"
          className="ui-link-btn"
          onClick={() => confirmDelete(student.id)}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <AppShell
      logoPosition="bottom"
      mainClassName="ui-shell__main--admin"
      contentClassName="ui-students-content"
      topbarActions={
        <TopbarActionGroup
          className="ui-topbar-actions--students"
          showDashboard
          onDashboard={() => navigate("/HomePage")}
          onLogout={handleLogout}
        />
      }
    >
      <CardPanel className="ui-card-panel--wide ui-students-panel ui-admin-panel">
        <div className="ui-admin-card">
          <PanelTitleRow title="Manage Students" />

          <FilterBar
            fields={[
              { name: "dateFrom", type: "date", placeholder: "Select Date" },
              { name: "dateTo", type: "date", placeholder: "Select Date" },
              { name: "studentName", placeholder: "Student Name" },
            ]}
            values={filters}
            onFieldChange={handleChange}
            onSubmit={handleSearch}
            onClear={handleClear}
            showClear
            submitLabel="Search"
            clearLabel="Clear"
            submitVariant="navy"
            className="ui-students-filter ui-admin-filter"
            extraActions={
              <>
                <ButtonBase
                  variant="blue"
                  className="ui-filter-bar__action-btn"
                  onClick={exportToPDF}
                >
                  Export to PDF
                </ButtonBase>
                <ButtonBase
                  variant="green"
                  className="ui-filter-bar__action-btn"
                  onClick={exportToExcel}
                >
                  Export to Excel
                </ButtonBase>
              </>
            }
          />

          {selectedStudents.length > 0 ? (
            <div className="ui-filter-bar__actions ui-students-selected-actions">
              <ButtonBase
                variant="danger"
                className="ui-filter-bar__action-btn ui-students-action-btn"
                onClick={confirmBulkDelete}
              >
                Delete Selected
              </ButtonBase>
            </div>
          ) : null}

          <DataTable
            columns={columns}
            data={students}
            className="ui-students-table"
            keyField="id"
            loading={loading}
            loadingText="Loading students..."
            emptyText="No students found."
          />

          <PaginationBar
            page={page}
            totalPages={totalPages}
            limit={limit}
            className="ui-students-pagination"
            onPageChange={handlePageChange}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </CardPanel>

      {studentToDelete !== null ? (
        <div className="ui-modal-overlay">
          <div className="ui-modal">
            <h2 className="ui-modal__title">Confirm Deletion</h2>
            <p className="ui-modal__row">
              Are you sure you want to delete this student? Once deleted, it
              cannot be restored.
            </p>
            <div className="ui-modal__actions">
              <ButtonBase
                variant="utility"
                onClick={() => setStudentToDelete(null)}
              >
                Cancel
              </ButtonBase>
              <ButtonBase variant="danger" onClick={handleDelete}>
                Delete
              </ButtonBase>
            </div>
          </div>
        </div>
      ) : null}

      {showBulkDeleteModal ? (
        <div className="ui-modal-overlay">
          <div className="ui-modal">
            <h2 className="ui-modal__title">Confirm Bulk Deletion</h2>
            <p className="ui-modal__row">
              Are you sure you want to delete the selected students? Once
              deleted, they cannot be restored.
            </p>
            <div className="ui-modal__actions">
              <ButtonBase
                variant="utility"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                Cancel
              </ButtonBase>
              <ButtonBase variant="danger" onClick={handleDeleteSelected}>
                Delete All
              </ButtonBase>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
};

export default ManageStudentPage;
